import React, { useState, useEffect } from 'react';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { getWeekStart, getWeekEnd, getWeekDays, formatDate, getDayAbbr, isToday } from '../utils/dateUtils';
import { normalizeSession, getBatchesArray, getStatusText } from '../utils/sessionUtils';
import { MdChevronLeft, MdChevronRight, MdCalendarToday, MdSchedule, MdSchool } from 'react-icons/md';

const statusStyles = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  marked: 'bg-green-50 text-green-800 border-green-200',
  missed: 'bg-red-50 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

const timeOrder = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!match) return Infinity;
  let hour = Number(match[1]);
  if (match[3]) hour = hour % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
  return hour * 60 + Number(match[2]);
};

/** Saturday-Friday calendar, with a compact date strip and daily session agenda. */
function WeeklyCalendar() {
  const { getToken } = useAttendanceAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [attendances, setAttendances] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const weekStart = getWeekStart(currentDate);
  const weekEnd = getWeekEnd(currentDate);
  const weekDays = getWeekDays(currentDate);
  const today = new Date();
  const todayKey = formatDate(today);
  const currentWeekKey = formatDate(getWeekStart(today));
  const requestDate = formatDate(weekStart);

  useEffect(() => {
    let active = true;
    const fetchCalendar = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await attendanceAPI.getCalendar({ date: requestDate }, getToken());
        if (!result.success || !Array.isArray(result.days)) {
          throw new Error(result.message || 'Unable to load your weekly schedule.');
        }
        const grouped = {};
        result.days.forEach(day => {
          if (day.date > todayKey) return;
          grouped[day.date] = (day.sessions || []).map(normalizeSession).filter(Boolean)
            .sort((a, b) => timeOrder(a.time_start) - timeOrder(b.time_start));
        });
        if (active) setAttendances(grouped);
      } catch (err) {
        if (active) {
          setError(err.message || 'Unable to load your weekly schedule.');
          setAttendances({});
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchCalendar();
    return () => { active = false; };
  }, [requestDate, getToken, reload, todayKey]);

  const changeWeek = (offset) => {
    setSelectedDay(null);
    setCurrentDate(previous => {
      const date = new Date(previous);
      date.setDate(date.getDate() + offset * 7);
      return date;
    });
  };
  const goToToday = () => {
    setSelectedDay(null);
    setCurrentDate(new Date());
    setReload(value => value + 1);
  };
  const visibleWeekDays = weekDays.filter(day => formatDate(day) <= todayKey);
  const canGoNext = requestDate < currentWeekKey;
  const allSessions = visibleWeekDays.flatMap(day => attendances[formatDate(day)] || []);
  const teachingDays = visibleWeekDays.filter(day => (attendances[formatDate(day)] || []).length);
  const displayedDays = selectedDay
    ? visibleWeekDays.filter(day => formatDate(day) === selectedDay)
    : teachingDays;
  const hasSessions = displayedDays.some(day => (attendances[formatDate(day)] || []).length);
  const visibleWeekEnd = weekEnd > today ? today : weekEnd;
  const rangeLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${visibleWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="mx-auto w-full max-w-7xl pb-8">
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-blue-600">Your teaching schedule</p>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Weekly Calendar</h1>
          <p className="mt-2 text-sm text-gray-500">Schedule history through today. Marking attendance is available only from Today&apos;s Attendance.</p>
        </div>
        <div className="flex items-center gap-2 self-start rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">
          <button type="button" aria-label="Previous week" onClick={() => changeWeek(-1)} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-500">
            <MdChevronLeft className="text-xl" />
          </button>
          <button type="button" onClick={goToToday} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500">Today</button>
          <button type="button" aria-label="Next week" onClick={() => changeWeek(1)} disabled={!canGoNext} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent">
            <MdChevronRight className="text-xl" />
          </button>
        </div>
      </header>

      <section aria-label="Choose a day" className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <MdCalendarToday className="shrink-0 text-blue-600" />
            <span>{rangeLabel}</span>
          </div>
          <button type="button" aria-pressed={!selectedDay} onClick={() => setSelectedDay(null)} className={`rounded-full px-3 py-1.5 text-xs font-semibold focus-visible:ring-2 focus-visible:ring-blue-500 ${!selectedDay ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>Whole week</button>
        </div>
        <div className="grid gap-1 p-2 sm:gap-2 sm:p-4" style={{ gridTemplateColumns: `repeat(${Math.max(visibleWeekDays.length, 1)}, minmax(0, 1fr))` }}>
          {visibleWeekDays.map(day => {
            const key = formatDate(day);
            const count = (attendances[key] || []).length;
            const selected = selectedDay === key;
            const today = isToday(day);
            return (
              <button key={key} type="button" aria-pressed={selected} aria-current={today ? 'date' : undefined}
                aria-label={`${day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${loading ? '' : `, ${count} sessions`}`}
                onClick={() => setSelectedDay(selected ? null : key)}
                className={`min-w-0 rounded-xl border px-1 py-3 text-center focus-visible:ring-2 focus-visible:ring-blue-500 sm:py-4 ${selected ? 'border-blue-600 bg-blue-600 text-white shadow-sm' : today ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50'}`}>
                <span className="block text-[10px] font-semibold uppercase tracking-wide sm:text-xs">{getDayAbbr(day)}</span>
                <span className="my-1 block text-xl font-bold sm:text-2xl">{day.getDate()}</span>
                <span className={`mx-auto block h-1.5 w-1.5 rounded-full ${!loading && count ? selected ? 'bg-white' : 'bg-blue-500' : 'bg-transparent'}`} />
                <span className="mt-2 hidden text-xs sm:block">{loading ? '…' : count ? `${count} ${count === 1 ? 'session' : 'sessions'}` : 'No sessions'}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section aria-label="Weekly sessions" aria-busy={loading} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
          <h2 className="font-semibold text-gray-900">{selectedDay ? 'Day schedule' : 'Week schedule'}</h2>
          {!loading && !error && <p className="text-xs text-gray-500">{allSessions.length} sessions this week · {teachingDays.length} scheduled days</p>}
        </div>

        {loading ? (
          <div role="status" className="flex min-h-[240px] items-center justify-center gap-3 text-sm text-gray-500">
            <MdSchedule className="text-xl text-blue-500" /> Loading your schedule…
          </div>
        ) : error ? (
          <div role="alert" className="px-6 py-12 text-center">
            <p className="font-semibold text-gray-900">We could not load your schedule</p>
            <p className="mt-2 text-sm text-red-600">{error}</p>
            <button type="button" onClick={() => setReload(value => value + 1)} className="mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Try again</button>
          </div>
        ) : !hasSessions ? (
          <div className="px-6 py-12 text-center sm:py-16">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><MdCalendarToday className="text-3xl" /></div>
            <h3 className="text-lg font-semibold text-gray-900">No sessions scheduled {selectedDay ? 'for this day' : 'this week'}</h3>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500">Your assigned classes will appear here on their scheduled dates.</p>
            {selectedDay && <button type="button" onClick={() => setSelectedDay(null)} className="mt-5 inline-flex items-center rounded-lg px-3 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50">View whole week</button>}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayedDays.map(day => (
              <div key={formatDate(day)} className="grid gap-4 p-4 sm:grid-cols-[120px_minmax(0,1fr)] sm:p-6">
                <div className="flex items-center gap-3 sm:block">
                  <p className="text-sm font-semibold text-gray-900">{day.toLocaleDateString('en-US', { weekday: 'long' })}</p>
                  <p className="text-sm text-gray-500 sm:mt-1">{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  {isToday(day) && <span className="inline-block rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 sm:mt-2">Today</span>}
                </div>
                <div className="grid min-w-0 gap-3 lg:grid-cols-2">
                  {(attendances[formatDate(day)] || []).map(session => {
                    const state = session.state?.toLowerCase() || 'pending';
                    return (
                      <article key={session.id}
                        className="min-w-0 rounded-xl border border-gray-200 border-l-4 border-l-blue-500 bg-white p-4 text-left">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500"><MdSchedule />{session.time_start || 'Time not set'}{session.time_end ? ` – ${session.time_end}` : ''}</span>
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusStyles[state] || statusStyles.pending}`}>{getStatusText(state)}</span>
                        </div>
                        <h3 className="break-words text-sm font-semibold leading-relaxed text-gray-900">{session.subject}</h3>
                        {getBatchesArray(session.batches).length > 0 && <p className="mt-2 flex items-start gap-1.5 break-words text-xs text-gray-500"><MdSchool className="mt-0.5 shrink-0" />{getBatchesArray(session.batches).join(', ')}</p>}
                        {session.period && <p className="mt-1 text-xs text-gray-500">{session.period}</p>}
                      </article>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default WeeklyCalendar;
